/**
 * Client for public/tts-worker/tts-worker.js (module worker).
 * Falls back gracefully when Worker / module workers are unavailable.
 *
 * IMPORTANT: always transfer a *copy* of Float32Arrays so the caller's
 * buffers stay intact if the worker fails and we fall back to main thread.
 */

import { LoadProgress } from './types';

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

let worker: any = null;
let workerOk: boolean | null = null;
let reqId = 1;
const pending: {
  [id: number]: {
    resolve: (v: any) => void;
    reject: (e: any) => void;
    onProgress?: (info: LoadProgress) => void;
  };
} = {};

function copyFloat32(src: Float32Array): Float32Array {
  const out = new Float32Array(src.length);
  out.set(src);
  return out;
}

export function isWorkerAvailable(): boolean {
  if (workerOk === false) return false;
  const win = getWin();
  return typeof win.Worker !== 'undefined';
}

function attachWorkerHandlers(w: any) {
  w.onmessage = (ev: any) => {
    const msg = ev.data || {};
    const id = msg.id;
    const slot = pending[id];
    if (!slot) return;
    if (msg.type === 'progress') {
      if (slot.onProgress) slot.onProgress(msg.payload || {});
      return;
    }
    delete pending[id];
    if (msg.type === 'error') {
      slot.reject(new Error(msg.error || 'Worker error'));
    } else {
      slot.resolve(msg.payload);
    }
  };
  w.onerror = (ev: any) => {
    workerOk = false;
    const detail =
      (ev && (ev.message || (ev.error && ev.error.message))) ||
      'TTS worker lỗi (thiếu COEP trên /tts-worker hoặc xem Console).';
    Object.keys(pending).forEach((k) => {
      const id = Number(k);
      if (pending[id]) {
        pending[id].reject(new Error(String(detail)));
        delete pending[id];
      }
    });
    try {
      worker && worker.terminate();
    } catch {
      /* ignore */
    }
    worker = null;
  };
  w.addEventListener &&
    w.addEventListener('messageerror', () => {
      /* ignore non-cloneable payloads */
    });
}

/** Blob fallback when COEP blocks URL workers (script served without COEP). */
async function createWorkerViaBlob(win: any): Promise<any> {
  const res = await win.fetch('/tts-worker/tts-worker.js', { cache: 'no-store' });
  if (!res.ok) throw new Error('Không tải được tts-worker.js (' + res.status + ').');
  let src = await res.text();
  // Rewrite relative imports so blob workers can still load sibling modules.
  src = src.replace(
    /import\(['"]\.\/vieneu-lite\.js['"]\)/g,
    "import('/tts-worker/vieneu-lite.js')"
  );
  const blob = new win.Blob([src], { type: 'text/javascript' });
  const url = win.URL.createObjectURL(blob);
  try {
    return new win.Worker(url, { type: 'module' });
  } finally {
    // Worker keeps its own reference; revoke after a tick.
    setTimeout(() => {
      try {
        win.URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }, 0);
  }
}

function probeWorker(w: any): Promise<boolean> {
  return new Promise((resolve) => {
    const id = reqId++;
    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(t);
      // Drop probe slot without rejecting callers
      delete pending[id];
      resolve(ok);
    };
    const t = setTimeout(() => done(false), 2500);
    // Any reply (result OR error) means the worker booted.
    pending[id] = {
      resolve: () => done(true),
      reject: () => done(true),
    };
    const prevErr = w.onerror;
    w.onerror = (ev: any) => {
      done(false);
      if (typeof prevErr === 'function') prevErr.call(w, ev);
    };
    try {
      w.postMessage({ id, action: '__probe__', payload: {} });
    } catch {
      done(false);
    }
  });
}

/** Prefer URL worker; if COEP blocks it, fall back to blob worker. */
export async function ensureWorkerReady(): Promise<any> {
  if (worker && workerOk) return worker;
  const win = getWin();
  let w: any = null;
  try {
    w = new win.Worker('/tts-worker/tts-worker.js?v=' + Date.now(), { type: 'module' });
    attachWorkerHandlers(w);
    worker = w;
    workerOk = true;
    const ok = await probeWorker(w);
    if (ok) return w;
  } catch {
    /* fall through */
  }
  try {
    if (w) w.terminate();
  } catch {
    /* ignore */
  }
  worker = null;
  workerOk = null;

  w = await createWorkerViaBlob(win);
  attachWorkerHandlers(w);
  worker = w;
  workerOk = true;
  const ok = await probeWorker(w);
  if (!ok) {
    workerOk = false;
    try {
      w.terminate();
    } catch {
      /* ignore */
    }
    worker = null;
    throw new Error(
      'TTS worker không khởi động được (COEP/COOP). Chạy qua scripts/serve-isolated.js hoặc cấu hình header cho /tts-worker.'
    );
  }
  return w;
}

export function callTtsWorker(
  action: string,
  payload: any,
  onProgress?: (info: LoadProgress) => void,
  signal?: { aborted: boolean }
): Promise<any> {
  return new Promise(async (resolve, reject) => {
    let w: any;
    try {
      w = await ensureWorkerReady();
    } catch (e) {
      reject(e);
      return;
    }
    const id = reqId++;
    pending[id] = { resolve, reject, onProgress };

    // Copy Float32Arrays before transfer so caller buffers stay usable on fallback
    const transfer: any[] = [];
    const body: any = Object.assign({}, payload);
    if (payload && payload.pcm instanceof Float32Array) {
      body.pcm = copyFloat32(payload.pcm);
      transfer.push(body.pcm.buffer);
    }
    if (payload && payload.referencePcm instanceof Float32Array) {
      body.referencePcm = copyFloat32(payload.referencePcm);
      transfer.push(body.referencePcm.buffer);
    }
    if (payload && payload.embedding instanceof Float32Array) {
      body.embedding = copyFloat32(payload.embedding);
      transfer.push(body.embedding.buffer);
    }

    const poll = setInterval(() => {
      if (signal && signal.aborted) {
        clearInterval(poll);
        try {
          w.postMessage({ id, action: 'cancel' });
        } catch {
          /* ignore */
        }
        if (pending[id]) {
          delete pending[id];
          reject(new Error('Đã hủy.'));
        }
      }
    }, 250);

    const origResolve = pending[id].resolve;
    const origReject = pending[id].reject;
    pending[id].resolve = (v) => {
      clearInterval(poll);
      origResolve(v);
    };
    pending[id].reject = (e) => {
      clearInterval(poll);
      origReject(e);
    };

    try {
      w.postMessage({ id, action, payload: body }, transfer);
    } catch (e) {
      clearInterval(poll);
      delete pending[id];
      reject(e);
    }
  });
}

export function terminateTtsWorker() {
  if (worker) {
    try {
      worker.terminate();
    } catch {
      /* ignore */
    }
  }
  worker = null;
  workerOk = null;
  Object.keys(pending).forEach((k) => {
    delete pending[Number(k)];
  });
}
